import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Users, DollarSign, Trophy, Medal, Wallet, ChevronLeft, ChevronRight } from 'lucide-react';
import soundEffects from '../../utils/soundEffects';
import { CarouselSurface } from '../carousel/CarouselSurface';
import { LoopingDeck, CarouselArrows, wrapCarouselIndex } from '../carousel/LoopingDeck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PlayModeSelector - Shown after course is selected
 * Swipeable carousel for selecting game/round type
 */
const PlayModeSelector = ({
  selectedCourse,
  numberOfHoles,
  onModeSelect,
  onGroupRound,  // New: callback to trigger group round setup
  suspended = false,
  disabled = false
}) => {
  const navigate = useNavigate();
  const [selectedMode, setSelectedMode] = useState(null);
  const [settingUp, setSettingUp] = useState(false);
  const creating = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  
  // Carousel state
  const [currentIndex, setCurrentIndex] = useState(0);
  const audioContextRef = useRef(null);

  const playModes = [
    {
      id: 'solo',
      title: 'Solo Practice',
      description: 'Track your own score',
      icon: User,
      color: 'green',
      glowColor: 'rgba(34, 197, 94, 0.5)'
    },
    {
      id: 'group',
      title: 'Group Round',
      description: 'Track your score, share with friends',
      icon: Users,
      color: 'blue',
      glowColor: 'rgba(59, 130, 246, 0.5)'
    },
    {
      id: 'skins',
      title: 'Skins Game',
      description: 'Lowest score wins the hole',
      icon: DollarSign,
      color: 'amber',
      glowColor: 'rgba(245, 158, 11, 0.5)'
    },
    {
      id: 'matchplay',
      title: 'Match Play',
      description: 'Team competition',
      icon: Trophy,
      color: 'purple',
      glowColor: 'rgba(168, 85, 247, 0.5)'
    },
    {
      id: 'strokeplay',
      title: 'Stroke Play',
      description: 'Tournament scoring',
      icon: Medal,
      color: 'indigo',
      glowColor: 'rgba(99, 102, 241, 0.5)'
    },
    {
      id: 'settleup',
      title: 'Settle Up',
      description: 'Pay & collect winnings',
      icon: Wallet,
      color: 'emerald',
      glowColor: 'rgba(16, 185, 129, 0.5)',
      isLink: true,
      linkTo: '/settlements'
    }
  ];

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    try {
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Resume audio context if suspended (required for mobile browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(250, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(60, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.06, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {}
  }, []);

  // Navigate carousel
  const goToCard = useCallback((index) => {
    const next = wrapCarouselIndex(index, playModes.length);
    if (next !== currentIndex) {
      playSwoosh();
      setCurrentIndex(next);
    }
  }, [currentIndex, playModes.length, playSwoosh]);

  const createOnce = async setup => {
    if (creating.current) return;
    creating.current = true;
    try { await setup(); } finally { creating.current = false; }
  };

  const handleModeClick = async (mode) => {
    if (disabled || creating.current) return;
    soundEffects.playSwish();
    
    // Handle direct link modes (Settle Up)
    if (mode.isLink && mode.linkTo) {
      navigate(mode.linkTo);
      return;
    }
    
    setSelectedMode(mode.id);

    // For solo, proceed directly to score entry
    if (mode.id === 'solo') {
      onModeSelect(mode.id);
    }
    
    // For group, trigger the group setup flow
    if (mode.id === 'group') {
      // Call the onGroupRound callback to show player selection
      if (onGroupRound) {
        onGroupRound();
      } else {
        // Fallback if no callback provided
        toast.info("Track your score now. Invite friends via Connect to compare rounds!", { duration: 4000 });
        onModeSelect(mode.id);
      }
    }
    
    // For skins, match play, stroke play - launch setup directly
    if (mode.id === 'skins') {
      return createOnce(handleSetupSkins);
    }
    
    if (mode.id === 'matchplay') {
      return createOnce(handleSetupMatchPlay);
    }
    
    if (mode.id === 'strokeplay') {
      return createOnce(handleSetupStrokePlay);
    }
  };

  const handleSetupSkins = async () => {
    if (!selectedCourse) {
      toast.error('Please select a course first');
      return;
    }

    setSettingUp(true);
    try {
      // Create a new skins event with the selected course
      const response = await axios.post(`${API}/skins/events`, {
        name: `Skins at ${selectedCourse.name}`,
        course_name: selectedCourse.name,
        course_id: selectedCourse.course_id,
        date: new Date().toISOString().split('T')[0],
        holes: numberOfHoles,
        skin_value: 5, // Default $5 per skin
        hole_pars: selectedCourse.hole_pars || []
      });

      if (response.data.event_id) {
        if (!mounted.current) return;
        toast.success('Skins game created!');
        // Navigate to the skins page with the event ID as query param
        navigate(`/tournaments/skins?event=${response.data.event_id}`);
      }
    } catch (error) {
      if (!mounted.current) return;
      console.error('Failed to create skins game');
      toast.error(error.response?.data?.detail || 'Failed to create skins game');
    } finally {
      if (mounted.current) setSettingUp(false);
    }
  };

  const handleSetupMatchPlay = async () => {
    if (!selectedCourse) {
      toast.error('Please select a course first');
      return;
    }

    setSettingUp(true);
    try {
      // Create a new match play event with the selected course
      const response = await axios.post(`${API}/match-play/events`, {
        name: `Match Play at ${selectedCourse.name}`,
        course_name: selectedCourse.name,
        course_id: selectedCourse.course_id,
        holes_type: numberOfHoles === 9 ? 'front' : 'full',
        total_weeks: 8
      });

      if (response.data.event_id) {
        if (!mounted.current) return;
        toast.success('Match Play league created!');
        // Navigate to the match play page with the event ID as query param
        navigate(`/tournaments/match-play?event=${response.data.event_id}`);
      }
    } catch (error) {
      if (!mounted.current) return;
      console.error('Failed to create match play');
      toast.error(error.response?.data?.detail || 'Failed to create match play');
    } finally {
      if (mounted.current) setSettingUp(false);
    }
  };

  const handleSetupStrokePlay = async () => {
    if (!selectedCourse) {
      toast.error('Please select a course first');
      return;
    }

    setSettingUp(true);
    try {
      // Create a new stroke play event with the selected course
      const response = await axios.post(`${API}/stroke-play/events`, {
        name: `Stroke Play at ${selectedCourse.name}`,
        course_name: selectedCourse.name,
        course_id: selectedCourse.course_id,
        date: new Date().toISOString().split('T')[0],
        holes: numberOfHoles,
        hole_pars: selectedCourse.hole_pars || [],
        hole_handicaps: selectedCourse.hole_handicaps || []
      });

      if (response.data.event_id) {
        if (!mounted.current) return;
        toast.success('Stroke Play event created!');
        // Navigate to the stroke play page with the event ID as query param
        navigate(`/tournaments/stroke-play?event=${response.data.event_id}`);
      }
    } catch (error) {
      if (!mounted.current) return;
      console.error('Failed to create stroke play');
      toast.error(error.response?.data?.detail || 'Failed to create stroke play event');
    } finally {
      if (mounted.current) setSettingUp(false);
    }
  };

  const getIconColorClasses = (color) => {
    const colors = {
      green: 'text-[var(--ps-primary)]',
      blue: 'text-[var(--ps-info)]',
      amber: 'text-amber-500',
      purple: 'text-purple-500',
      indigo: 'text-indigo-500',
      emerald: 'text-emerald-500'
    };
    return colors[color];
  };

  const getGradient = (color) => {
    const gradients = {
      green: 'from-green-900/90 to-green-800/50',
      blue: 'from-blue-900/90 to-blue-800/50',
      amber: 'from-amber-900/90 to-amber-800/50',
      purple: 'from-purple-900/90 to-purple-800/50',
      indigo: 'from-indigo-900/90 to-indigo-800/50',
      emerald: 'from-emerald-900/90 to-emerald-800/50'
    };
    return gradients[color];
  };

  const getBorderColor = (color) => {
    const borders = {
      green: 'border-green-500/60',
      blue: 'border-blue-500/60',
      amber: 'border-amber-500/60',
      purple: 'border-purple-500/60',
      indigo: 'border-indigo-500/60',
      emerald: 'border-emerald-500/60'
    };
    return borders[color];
  };

  return <CarouselSurface id="play-mode-carousel" label="Choose a game" open={!suspended}
    onExit={() => { mounted.current = false; navigate('/dashboard', { state: { carouselDismissed: true } }); }}>
    <div className="carousel-panel-header text-white">
      <h3 className="carousel-panel-title" data-testid="play-mode-title">Choose your game</h3>
      <p className="text-sm text-gray-300" data-testid="play-mode-course">{selectedCourse?.name || 'Your course'}</p>
    </div>
    <LoopingDeck id="play-mode-deck" items={playModes} index={currentIndex} onIndexChange={goToCard} actionCards
      renderItem={(mode, active) => {
        const IconComponent = mode.icon;
        return <button type="button" data-testid={`play-mode-${mode.id}`} disabled={disabled || settingUp}
          onClick={() => { if (active) handleModeClick(mode); }}
          className={`carousel-action-card bg-gradient-to-br ${getGradient(mode.color)} ${getBorderColor(mode.color)}`}
          style={{ boxShadow: active ? `0 10px 30px ${mode.glowColor.replace('0.5', '0.2')}` : 'none' }}>
          {settingUp && selectedMode === mode.id ? <div data-testid="play-mode-creating" className="w-8 h-8 mx-auto border-2 border-white border-t-transparent rounded-full animate-spin" /> :
            <div className="flex flex-col items-center justify-center text-center gap-2">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-black/30"><IconComponent className={`w-10 h-10 ${getIconColorClasses(mode.color)}`} /></div>
              <h4 className="font-bold text-lg text-white">{mode.title}</h4>
              <p className="text-sm text-gray-300">{mode.description}</p>
            </div>}
        </button>;
      }} />
    <CarouselArrows id="play-mode" previous={() => goToCard(currentIndex - 1)} next={() => goToCard(currentIndex + 1)} />
  </CarouselSurface>;
};

export default PlayModeSelector;
